import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import './EmailTemplateEditor.css';

const EMAIL_PREVIEW_WIDTH = 600;

const PREVIEW_ZOOM_OPTIONS = [
  { id: 'fit', label: '全体表示' },
  { id: '100', label: '100%' },
  { id: '125', label: '125%' },
  { id: '150', label: '150%' },
];

/** iframe 内メール HTML の実高さ（テーブルレイアウト対応） */
function measureEmailIframeHeight(iframe) {
  let doc;
  try {
    doc = iframe.contentDocument;
  } catch {
    return null;
  }
  if (!doc?.body) return null;

  iframe.style.display = 'block';
  iframe.style.width = `${EMAIL_PREVIEW_WIDTH}px`;
  iframe.style.maxWidth = `${EMAIL_PREVIEW_WIDTH}px`;
  iframe.style.border = 'none';
  iframe.style.height = '8192px';
  iframe.style.minHeight = '0';

  let marginStyle = doc.getElementById('ete-preview-style');
  if (!marginStyle) {
    marginStyle = doc.createElement('style');
    marginStyle.id = 'ete-preview-style';
    marginStyle.textContent = 'html, body { margin: 0; padding: 0; }';
    doc.head.appendChild(marginStyle);
  }
  doc.getElementById('ete-measure-style')?.remove();

  void doc.body.offsetHeight;

  const bodyTop = doc.body.getBoundingClientRect().top;
  let maxBottom = 0;
  doc.body.querySelectorAll('body *').forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.height > 0) {
      maxBottom = Math.max(maxBottom, rect.bottom - bodyTop);
    }
  });

  const height = Math.ceil(Math.max(maxBottom, 120));
  iframe.style.height = `${height}px`;
  iframe.setAttribute('height', String(height));
  return height;
}

/**
 * メールテンプレート プレビュー専用ビューア（実送信と同一 HTML）
 */
const EmailTemplateEditor = ({
  open,
  templateKey,
  meta,
  loading,
  message,
  previewHtml,
  previewSubject,
  previewLoading,
  previewRenderedBy,
  onClose,
  onRefreshPreview,
}) => {
  const previewIframeRef = useRef(null);
  const previewViewportRef = useRef(null);
  const [previewZoom, setPreviewZoom] = useState('fit');
  const [previewContentHeight, setPreviewContentHeight] = useState(null);
  const [previewFitScale, setPreviewFitScale] = useState(1);

  const previewScale = previewZoom === 'fit'
    ? previewFitScale
    : Number(previewZoom) / 100;

  const previewDisplayHeight = previewContentHeight ?? 480;

  const measurePreviewContent = useCallback(() => {
    const iframe = previewIframeRef.current;
    const viewport = previewViewportRef.current;
    if (!iframe?.contentDocument || !viewport) return;

    const height = measureEmailIframeHeight(iframe);
    if (!height) return;

    setPreviewContentHeight(height);

    const pad = 32;
    const vpH = Math.max(viewport.clientHeight - pad, 80);
    const vpW = Math.max(viewport.clientWidth - pad, 120);
    const scaleH = vpH / height;
    const scaleW = vpW / EMAIL_PREVIEW_WIDTH;
    setPreviewFitScale(Math.min(scaleH, scaleW, 1));
  }, []);

  const schedulePreviewMeasure = useCallback(() => {
    measurePreviewContent();
    requestAnimationFrame(() => {
      measurePreviewContent();
      requestAnimationFrame(measurePreviewContent);
    });
    window.setTimeout(measurePreviewContent, 250);
    window.setTimeout(measurePreviewContent, 800);
  }, [measurePreviewContent]);

  const handlePreviewIframeLoad = useCallback(() => {
    schedulePreviewMeasure();
  }, [schedulePreviewMeasure]);

  useEffect(() => {
    if (!open) return undefined;
    const viewport = previewViewportRef.current;
    if (!viewport) return undefined;
    const ro = new ResizeObserver(() => schedulePreviewMeasure());
    ro.observe(viewport);
    return () => ro.disconnect();
  }, [open, schedulePreviewMeasure, previewHtml]);

  useEffect(() => {
    if (open) {
      setPreviewZoom('fit');
      setPreviewContentHeight(null);
    }
  }, [open, templateKey]);

  useEffect(() => {
    if (!previewHtml || previewLoading) return undefined;
    setPreviewContentHeight(null);
    const id = window.setTimeout(schedulePreviewMeasure, 100);
    return () => window.clearTimeout(id);
  }, [previewHtml, previewLoading, schedulePreviewMeasure]);

  useEffect(() => {
    schedulePreviewMeasure();
  }, [previewZoom, schedulePreviewMeasure]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const showPreviewLoading = previewLoading && !previewHtml;
  const isUserHub = meta?.hub === 'userhub' || previewRenderedBy === 'userhub';
  const hubLabel = isUserHub ? 'UserHub' : 'AdminHub';
  const renderSourceLabel = isUserHub
    ? 'UserHub コード正本（internal preview）'
    : 'AdminHub コード正本';

  const modal = (
    <div className="ete-overlay" role="presentation">
      <div
        className={`ete-shell ete-shell-preview-only ${isUserHub ? 'ete-shell-userhub' : 'ete-shell-adminhub'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ete-title"
      >
        <header className="ete-header">
          <div className="ete-header-main">
            <div className="ete-header-icon" aria-hidden="true">✉</div>
            <div className="ete-header-text">
              <h2 id="ete-title" className="ete-title">{meta?.label || templateKey}</h2>
              <div className="ete-header-meta">
                <code className="ete-key-chip">{templateKey}</code>
                <span className={`ete-hub-chip ${isUserHub ? 'userhub' : 'admin'}`}>
                  {isUserHub ? 'UserHub 管轄' : 'AdminHub 管轄'}
                </span>
                <span className="ete-kind-chip">{meta?.kind === 'full-html' ? 'フル HTML' : 'Hub ラップ'}</span>
                <span className="ete-from-chip">from: {meta?.from || 'info'}</span>
                <span className={`ete-source-chip ${isUserHub ? 'userhub' : 'admin'}`}>{renderSourceLabel}</span>
              </div>
            </div>
          </div>
          <div className="ete-header-actions">
            <button type="button" className="ete-btn ghost" onClick={onClose} aria-label="閉じる">
              ✕
            </button>
          </div>
        </header>

        {loading ? (
          <div className="ete-loading">
            <div className="ete-loading-spinner" />
            <p>プレビューを読み込み中…</p>
          </div>
        ) : (
          <>
            <div className="ete-toolbar">
              <div className="ete-toolbar-group">
                <span className="ete-toolbar-label">表示倍率</span>
                {PREVIEW_ZOOM_OPTIONS.map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    className={`ete-seg-btn ${previewZoom === id ? 'active' : ''}`}
                    onClick={() => setPreviewZoom(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="ete-toolbar-group ete-toolbar-spacer">
                <button
                  type="button"
                  className="ete-btn subtle"
                  onClick={() => onRefreshPreview?.(templateKey)}
                >
                  再読み込み
                </button>
              </div>
            </div>

            {message && (
              <div className={`ete-banner ${message.type}`}>{message.text}</div>
            )}

            <div className={`ete-chrome-bar ${isUserHub ? 'userhub' : 'admin'}`}>
              <p className="ete-chrome-meta">
                {meta?.label || templateKey}（{templateKey}） / 管轄: {hubLabel} / 描画: {renderSourceLabel}
              </p>
              {isUserHub && (
                <p className="ete-chrome-note">この HTML は UserHub 側の実送信経路と同一です（AdminHub では描画していません）</p>
              )}
              <p className="ete-chrome-subject">
                <span>件名</span>
                {previewSubject || '—'}
              </p>
            </div>

            <div className="ete-workspace layout-preview">
              <section className="ete-preview-pane">
                <div
                  ref={previewViewportRef}
                  className={`ete-preview-viewport ${previewZoom === 'fit' ? 'mode-fit' : 'mode-zoom'}`}
                >
                  {showPreviewLoading ? (
                    <div className="ete-preview-empty">
                      <div className="ete-loading-spinner small" />
                      <p>プレビューを生成しています…</p>
                    </div>
                  ) : (
                    <div className="ete-preview-stage">
                      <div
                        className="ete-preview-fit-wrap"
                        style={{
                          width: EMAIL_PREVIEW_WIDTH * previewScale,
                          height: previewDisplayHeight * previewScale,
                        }}
                      >
                        <div
                          className="ete-preview-scaler"
                          style={{
                            width: EMAIL_PREVIEW_WIDTH,
                            height: previewDisplayHeight,
                            transform: `scale(${previewScale})`,
                          }}
                        >
                          <iframe
                            ref={previewIframeRef}
                            title="メール送信プレビュー"
                            className="ete-preview-frame"
                            sandbox="allow-same-origin"
                            width={EMAIL_PREVIEW_WIDTH}
                            height={previewDisplayHeight}
                            srcDoc={previewHtml || '<p style="padding:24px;color:#6b7280;">プレビューがありません</p>'}
                            onLoad={handlePreviewIframeLoad}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>

            <footer className="ete-footer">
              <div className="ete-footer-left">
                <span className="ete-footer-note">
                  {isUserHub
                    ? '表示は UserHub 実送信と同一の HTML です（編集不可）'
                    : '表示は AdminHub 実送信と同一の HTML です（編集不可）'}
                </span>
              </div>
              <div className="ete-footer-right">
                <button type="button" className="ete-btn secondary" onClick={onClose}>
                  閉じる
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default EmailTemplateEditor;
